import React from "react";
import { useSelector } from "react-redux";
import {
  styleClasses,
  StyledCard,
  ProductGroupStyledDiv,
  StyledTypography,
  StyledPriceDiv,
  StyledQuantityInput,
  ImageContainer,
  ProductGroupComponentsImageContainer,
} from "./ProductGroupStyles";
import { PLATFORM_DEFAULTS } from "../../utils/DefaultConfigs";
import { isAbsolutePath } from "../../utils/ProductUtil";
import { useIntl } from "react-intl";
import {
  selectConfigValue,
  selectProductImagePrefix,
} from "../../store/app-config/AppConfigSelectors";
import AppState from "../../store/AppState";
import { AdapterInfoObject } from "../../utils/AppConfigUtils";
import { selectAdapterInfo } from "../../store/adapter-config/AdapterConfigSelectors";
import NoImageIcon from "../../assets/NoImage.png";
import TooltipComponent from "components/common/ToolTip";
import { CDN_WIDGET_URL, removeDotFromStart } from "utils/WidgetUtil";
import { Product } from "store/product/ProductTypes";
import PublishControl from "components/PublishControl";
import { ProductGroupComponent } from "store/product-group/ProductGroupComponentsTypes";

interface Props {
  product: Product | null;
  code?: string;
  productGroupId?: string;
  height?: string;
  isColumn: boolean;
  price?: string;
  index?: Number;
  storeId?: string;
  productGroupPrice?: string;
  isPublished?: boolean;
  productGroupComponents?: ProductGroupComponent[];
  hasComponentQuantity?: boolean;
}

const ProductGroupComponents: React.FC<Props> = (props) => {
  const {
    product,
    isColumn,
    index,
    price,
    productGroupId,
    productGroupPrice,
    isPublished,
    productGroupComponents,
    hasComponentQuantity,
    ...otherProps
  } = props;

  const intl = useIntl();
  const storeId = props?.storeId ?? "";
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const ecomPlatformType = adapterInfo?.adapterId?.toUpperCase() ?? "";
  const platformDefaults = PLATFORM_DEFAULTS[ecomPlatformType];
  const productImagePrefix = useSelector((state: AppState) =>
    selectProductImagePrefix(state, storeId),
  );
  const storeAndAccountInventory = useSelector((state: AppState) =>
    selectConfigValue(state, "inventoryThreshold"),
  );
  const inventoryThreshold = parseInt(
    storeAndAccountInventory !== ""
      ? storeAndAccountInventory
      : (platformDefaults?.inventoryThreshold ?? ""),
  );

  const productImage = product?.thumbnail;
  const imagePath = isAbsolutePath(productImage)
    ? productImage
    : productImagePrefix + productImage;
  const productType = product?.typeCode;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  // Helper function to render image with fallback
  const renderImage = (
    imageSrc: string | undefined,
    altText: string,
    className: string,
  ): React.ReactElement => {
    if (imageSrc) {
      return (
        <img
          className={className}
          src={imageSrc}
          alt={altText}
          draggable={false}
        />
      );
    }
    return (
      <ProductGroupStyledDiv className={styleClasses.emptyImageContainer}>
        <img
          className="image"
          src={`${CDN_WIDGET_URL}${removeDotFromStart(NoImageIcon)}`}
          alt={altText}
          draggable={false}
        />
      </ProductGroupStyledDiv>
    );
  };

  // Helper function to render stock status
  const renderStockStatus = (
    stock: number,
    className: string,
    message: string,
  ): React.ReactElement => (
    <ProductGroupStyledDiv className={className}>
      <StyledTypography className={styleClasses.stock}>
        {message}
      </StyledTypography>
    </ProductGroupStyledDiv>
  );

  // Helper function to render tooltip-wrapped content
  const renderWithTooltip = (
    content: React.ReactElement,
    tooltipTitle: string,
  ): React.ReactElement => (
    <TooltipComponent tooltipTitle={tooltipTitle} placement="bottom">
      {content}
    </TooltipComponent>
  );

  // Helper function to render typography with isColumn prop
  const renderTypography = (
    className: string,
    text: string,
  ): React.ReactElement => (
    <StyledTypography className={className} isColumn={isColumn}>
      {text}
    </StyledTypography>
  );

  const renderProductImage = (
    isColumn: boolean,
    productImage: string | undefined,
    product: Product | null,
  ) => {
    const Container = isColumn
      ? ProductGroupComponentsImageContainer
      : ImageContainer;
    const imageClassName = isColumn ? "image" : "productGroupImage";

    return (
      <Container>
        {renderImage(productImage, product?.name || "", imageClassName)}
      </Container>
    );
  };

  const renderProductInfo = (
    isColumn: boolean,
    product: Product | null,
    price?: string,
  ) => {
    if (!product) return null;

    const productPrice = isColumn ? price || "" : productGroupPrice || "";

    const quantity = productGroupComponents?.find(
      (component) => component.productId === product.productId,
    )?.quantity;

    return (
      <ProductGroupStyledDiv
        className={styleClasses.imageProductCodeDiv}
        isColumn={isColumn}
      >
        {renderWithTooltip(
          renderTypography(styleClasses.productName, product.name || ""),
          product.name || "",
        )}

        {product.code &&
          isColumn &&
          renderWithTooltip(
            renderTypography(styleClasses.productCode, product.code),
            product.code,
          )}

        {renderPriceSection(productPrice)}

        {hasComponentQuantity && (
          <StyledQuantityInput
            className={styleClasses.productQuantity}
            variant="outlined"
            size="small"
            value={quantity}
            disabled
            inputProps={{
              min: 1,
              style: { textAlign: "center" },
            }}
          />
        )}
      </ProductGroupStyledDiv>
    );
  };

  const renderProductType = () => (
    <StyledTypography className={styleClasses.productType}>
      {productType}
    </StyledTypography>
  );

  const renderProductSetPrice = (productPrice: string) => (
    <StyledPriceDiv className={styleClasses.productPriceContainer}>
      <StyledTypography
        isColumn={isColumn}
        className={styleClasses.productPriceGroup}
      >
        Starting from
      </StyledTypography>
      {renderTypography(styleClasses.productPrice, productPrice)}
    </StyledPriceDiv>
  );

  const renderRegularPrice = (productPrice: string) =>
    renderTypography(styleClasses.productPrice, productPrice);

  const renderPriceSection = (productPrice: string) => {
    const priceContent =
      !isColumn && product?.typeCode === "Product Set"
        ? renderProductSetPrice(productPrice)
        : renderRegularPrice(productPrice);

    return renderWithTooltip(priceContent, productPrice);
  };

  // Helper function to determine stock status
  const getStockStatus = () => {
    const stock = Number(product?.stock);

    if (stock <= 0) {
      return {
        className: styleClasses.outOfStock,
        message: intl.formatMessage({
          id: "colorManagement.noStockItem",
          defaultMessage: "No stock Item",
        }),
      };
    }

    if (stock <= inventoryThreshold) {
      return {
        className: styleClasses.belowInventoryThreshold,
        message: `INV:${stock}`,
      };
    }

    return {
      className: styleClasses.thresholdRange,
      message: `INV:${stock}`,
    };
  };

  return (
    <ProductGroupStyledDiv>
      <StyledCard
        variant="outlined"
        className={
          !isColumn
            ? styleClasses.productGroupCard
            : styleClasses.productGroupComponentCard
        }
        {...otherProps}
      >
        <ProductGroupStyledDiv className={styleClasses.cardContentDiv}>
          {isColumn &&
            product &&
            (() => {
              const stockStatus = getStockStatus();
              return renderStockStatus(
                Number(product.stock),
                stockStatus.className,
                stockStatus.message,
              );
            })()}

          {!isColumn && renderProductType()}

          {renderProductImage(isColumn, imagePath, product)}

          {renderProductInfo(isColumn, product, price)}

          {isColumn && (
            <ProductGroupStyledDiv
              className={
                !hasComponentQuantity ? styleClasses.publishControlWrapper : ""
              }
            >
              <PublishControl
                onChange={handleCheckboxChange}
                flag={isPublished}
                product={product}
                isPublishedFromProductGroup={false}
              />
            </ProductGroupStyledDiv>
          )}
        </ProductGroupStyledDiv>
      </StyledCard>
    </ProductGroupStyledDiv>
  );
};

export default ProductGroupComponents;
